import { EntityLocationDTO } from '@user/store/map/typings'
import { MapPinTypeVariant } from '@user/components/HereMap/MapPin/types'
import React from 'react'
import { useData } from '@user/components/HereMap/MapPin/components/MapPinTooltip/hooks/useData'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import MapBubbleContentsSkeleton from '@user/features/MapView/MapBubbleContents/MapBubbleContentsSkeleton'
import MapBubbleContentsRestricted from '@user/features/MapView/MapBubbleContents/MapBubbleContentsRestricted'
import MapBubbleContentsAlert from '@user/features/MapView/MapBubbleContents/MapBubbleContentsAlert'
import MapBubbleContentsLoaded from '@user/features/MapView/MapBubbleContents/MapBubbleContentsLoaded'
import MapBubbleContentsPestPac from '@user/features/MapView/MapBubbleContents/MapBubbleContentsPestPac'
import { MapBubbleMultiTenantContents } from '@user/features/MapView/MapBubbleContents/MapBubbleMultiTenantContents'
import { useMapLibreTooltipPosition } from './hooks/useMapLibreTooltipPosition'
import { useMeasuredTooltipHeight } from './hooks/useMeasuredTooltipHeight'
import maplibregl from 'maplibre-gl'

type MapLibreMapPinTooltipProps = {
  contactLocation: EntityLocationDTO
  map: maplibregl.Map
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  onActionClick: (action: string, contactLocation: EntityLocationDTO) => void
  onClose: () => void
  onKnockStatusCleared?: () => void
}

const TOOLTIP_WIDTH = 390

function arrowStyle(position: string, arrowOffset: number) {
  if (position === 'right')
    return { left: '-11px', top: `calc(50% + ${arrowOffset}px - 30px)` }
  if (position === 'left')
    return { right: '-11px', top: `calc(50% + ${arrowOffset}px - 30px)` }
  if (position === 'bottom')
    return { top: '-8px', left: `calc(50% + ${arrowOffset}px - 15px)` }
  return { bottom: '-14px', left: `calc(50% + ${arrowOffset}px - 15px)` } // top
}

export const MapLibreMapPinTooltip: React.FC<MapLibreMapPinTooltipProps> = ({
  contactLocation,
  map,
  onMouseEnter,
  onMouseLeave,
  onActionClick,
  onClose,
  onKnockStatusCleared,
}) => {
  const { height: tooltipHeight, measureRef } = useMeasuredTooltipHeight(350)

  const { position, arrowOffset, left, top, transform, isOutOfViewport } =
    useMapLibreTooltipPosition({
      map,
      latitude: contactLocation.latitude,
      longitude: contactLocation.longitude,
      tooltipWidth: TOOLTIP_WIDTH,
      tooltipHeight,
    })

  const {
    entityStatus,
    fallbackEntityDetailsStatus,
    fallbackLocation,
    selectedEntity,
  } = useData({ location: contactLocation })
  const isPestPacPin =
    contactLocation.entityType?.toLowerCase() === MapPinTypeVariant.PestPac

  if (isOutOfViewport) return null

  return (
    <div
      ref={measureRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: 'absolute',
        left,
        top,
        transform,
        backgroundColor: 'white',
        paddingLeft: '24px',
        paddingRight: '24px',
        paddingTop: '16px',
        paddingBottom: '22px',
        borderRadius: 8,
        pointerEvents: 'auto',
        zIndex: 1000,
        minWidth: '380px',
        maxWidth: '400px',
        boxShadow:
          '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
      }}
    >
      {contactLocation.multitenant ? (
        <>
          <MapBubbleMultiTenantContents
            onViewClick={() =>
              onActionClick('multiTenantView', contactLocation)
            }
            onClose={onClose}
            showCloseBtn
            formattedAddress={contactLocation.formattedAddress}
          />
          <Box
            sx={{
              position: 'absolute',
              ...arrowStyle(position, arrowOffset),
              margin: 0,
              backgroundColor: 'white',
              width: '30px',
              height: '30px',
              transform: 'rotate(45deg)',
              zIndex: -1,
            }}
          />
        </>
      ) : (
        <Stack sx={{ userSelect: 'none' }}>
          {isPestPacPin ? (
            <MapBubbleContentsPestPac
              location={contactLocation}
              onClose={onClose}
              showCloseBtn
              onKnockStatusClick={() =>
                onActionClick('knockStatusClickMapper', contactLocation)
              }
              onAddLeadClick={() =>
                onActionClick('pestPacAddLead', contactLocation)
              }
              onKnockStatusCleared={onKnockStatusCleared}
              onViewClick={() => onActionClick('viewClick', contactLocation)}
              onViewKnockStatusHistory={(knockStatusLocationId) =>
                onActionClick('knockStatusHistoryClickMapper', {
                  ...contactLocation,
                  knockStatusLocationId,
                })
              }
            />
          ) : entityStatus === 'empty' ||
            entityStatus === 'loading' ||
            (entityStatus === 'error' &&
              fallbackEntityDetailsStatus === 'loading') ? (
            <Box pb={0}>
              <MapBubbleContentsSkeleton />
            </Box>
          ) : entityStatus === 'error' ? (
            fallbackLocation ? (
              <MapBubbleContentsRestricted
                location={fallbackLocation}
                onClose={onClose}
                showCloseBtn
              />
            ) : (
              <MapBubbleContentsAlert
                message="An unknown error occurred"
                severity="error"
              />
            )
          ) : (
            selectedEntity && (
              <MapBubbleContentsLoaded
                onNameClick={() => onActionClick('nameClick', contactLocation)}
                onViewClick={() => onActionClick('viewClick', contactLocation)}
                onKnockStatusClick={() =>
                  onActionClick('knockStatusClickMapper', contactLocation)
                }
                onKnockStatusCleared={onKnockStatusCleared}
                onViewKnockStatusHistory={() =>
                  onActionClick(
                    'knockStatusHistoryClickMapper',
                    contactLocation,
                  )
                }
                onClose={onClose}
                showCloseBtn
              />
            )
          )}

          <Box
            sx={{
              position: 'absolute',
              ...arrowStyle(position, arrowOffset),
              margin: 0,
              backgroundColor: 'white',
              width: '30px',
              height: '30px',
              transform: 'rotate(45deg)',
              zIndex: -1,
            }}
          />
        </Stack>
      )}
    </div>
  )
}
